/**
 * Get Normalized URL
 *
 * The current page URL, reduced to the identity the widget matches on: no
 * protocol, no `www.`, no trailing slash, campaign parameters removed and the
 * rest sorted. Display rules, `customerUrl` on outbound messages and the
 * launcher's page-change watch all compare these strings, so two URLs that
 * differ only in campaign tracking or argument order have to normalize alike.
 *
 * `urlDisplayRuleMatcher` compares a configured rule to this string with a bare
 * `===`/`indexOf` and normalizes nothing on the rule side, so every step below is
 * load-bearing: a rule is authored against one exact spelling of the page URL,
 * and changing the spelling silently stops matching it. The steps reproduce
 * `normalize-url@8`, which produced these strings before it was removed —
 * `getNormalizedUrl.test.ts` pins each one against a case that library decided.
 */

import { logger } from '@/lib/logger';

export default function getNormalizedUrl(): string {
  try {
    const url = new URL(window.location.toString());

    // A rooted FQDN is the same host as the unrooted form, and the `www.` test
    // below only clears a hostname whose trailing dot has already gone.
    url.hostname = url.hostname.replace(/\.$/, '');
    if (/^www\.(?!www\.)[a-z\-\d]{1,63}\.[a-z.\-\d]{2,63}$/.test(url.hostname)) {
      url.hostname = url.hostname.replace(/^www\./, '');
    }

    // A scroll-to-text fragment is a browser affordance the page never sees.
    url.hash = url.hash.replace(/#?:~:text.*?$/i, '');

    // Collapse repeated slashes, except the pair inside a URL embedded in the
    // path (`/r/https://foo.com/x`). Matching the protocol first consumes its own
    // `//`, which is what keeps `/https:///x` intact — a negative lookbehind
    // reads that run as one and over-collapses it.
    url.pathname = url.pathname.replace(/\b[a-z][a-z\d+\-.]{1,50}:\/\/|\/{2,}/g, (m) => (m.startsWith('/') ? '/' : m));
    try {
      // Percent-encoded octets that need no encoding come back as characters, so
      // `/t-shirt-%28large%29` matches a rule written `/t-shirt-(large)`. The
      // assignment re-encodes whatever still has to be, such as spaces.
      url.pathname = decodeURI(url.pathname).replace(/\\/g, '%5C');
    } catch {
      // A malformed escape sequence: keep the encoded form rather than lose the path.
    }

    // Campaign parameters describe how the shopper arrived, not which page they
    // are on. Left in, every paid landing would read as a distinct URL and a
    // display rule written against the clean path would stop matching it.
    for (const key of [...url.searchParams.keys()]) {
      if (/^utm_\w+/i.test(key)) {
        url.searchParams.delete(key);
      }
    }

    const originalSearch = url.search;
    url.searchParams.sort();
    try {
      // `sort()` re-serializes through URLSearchParams, which percent-encodes
      // characters the address bar left alone. Decoding restores the form a rule
      // author would have copied out of the browser.
      url.search = decodeURIComponent(url.search);
    } catch {
      // A malformed escape sequence: keep the encoded form rather than lose the query.
    }
    // That same re-serialization gives a valueless parameter an `=`, turning
    // `?checkout` into `?checkout=`. Put the originals back.
    for (const part of originalSearch
      .slice(1)
      .split('&')
      .filter((p) => p && !p.includes('='))) {
      const decoded = decodeURIComponent(part);
      url.search = url.search.replace(`?${decoded}=`, `?${decoded}`).replace(`&${decoded}=`, `&${decoded}`);
    }

    // Two separate trims, and the second is not on the path: a trailing slash
    // goes from the end of the whole URL, so a query ending in one loses it too —
    // but only when there is no hash to sit behind.
    url.pathname = url.pathname.replace(/\/$/, '');
    let serialized = url.toString();
    if (url.hash === '') {
      serialized = serialized.replace(/\/$/, '');
    }

    const out = new URL(serialized);
    return `${out.hostname}${out.pathname.replace(/\/*$/, '')}${out.search}${out.hash}`;
  } catch (error) {
    logger.error('Error getting normalized URL:', error);
    return '';
  }
}
